import { Box, Button, Flex, Icon, Select, Stack, Text } from '@chakra-ui/react';
import { Button as SharedButton, TextField } from '@diamond/shared/ui';
import {
  PaymentInstructionTelemetryContext,
  usePaymentProofForm,
  usePaymentProofOcrForm,
} from '@diamond/sol-buyer/utils';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { useCallback, useState } from 'react';
import { FormProvider } from 'react-hook-form';

import { BANK_OPTIONS } from '../order-detail/PaymentProofUploaderOcr/banks.constant';
import { DropzoneOcr } from '../order-detail/PaymentProofUploaderOcr/DropzoneOcr';
import {
  getRemainingOcrFileSlots,
  getVisibleOcrCards,
  isOcrUploadLoadingState,
} from '../order-detail/PaymentProofUploaderOcr/ocr-staging';
import { OcrStagedCards } from '../order-detail/PaymentProofUploaderOcr/OcrStagedCards';
import { OcrStagedDropzone } from '../order-detail/PaymentProofUploaderOcr/OcrStagedDropzone';
import { OcrUploadLoadingModal } from '../order-detail/PaymentProofUploaderOcr/OcrUploadLoadingModal';
import { useOcrDropzoneOpenRequest } from '../order-detail/PaymentProofUploaderOcr/useOcrDropzoneOpenRequest';

interface PaymentInstructionProofPanelProps {
  accessToken: string;
  mode: 'ocr' | 'manual';
  orderPaymentId: string;
  onSuccess?: () => void;
  onUploadStarted?: () => void;
  openUploadRequestKey?: number;
  telemetryContext?: PaymentInstructionTelemetryContext;
}

const INSTRUCTION_DROPZONE_MAX_W = '100%';

export const PaymentInstructionProofPanel = ({
  accessToken,
  mode,
  orderPaymentId,
  onSuccess,
  onUploadStarted,
  openUploadRequestKey = 0,
  telemetryContext,
}: PaymentInstructionProofPanelProps) => {
  switch (mode) {
    case 'manual':
      return (
        <PaymentInstructionManualForm
          accessToken={accessToken}
          orderPaymentId={orderPaymentId}
          telemetryContext={telemetryContext}
          onSuccess={onSuccess}
        />
      );
    case 'ocr':
      return (
        <PaymentInstructionOcrForm
          accessToken={accessToken}
          openUploadRequestKey={openUploadRequestKey}
          orderPaymentId={orderPaymentId}
          telemetryContext={telemetryContext}
          onSuccess={onSuccess}
          onUploadStarted={onUploadStarted}
        />
      );
    default: {
      const _exhaustive: never = mode;
      return _exhaustive;
    }
  }
};

const PaymentInstructionOcrForm = ({
  accessToken,
  onSuccess,
  onUploadStarted,
  openUploadRequestKey,
  orderPaymentId,
  telemetryContext,
}: Omit<PaymentInstructionProofPanelProps, 'mode'> & {
  openUploadRequestKey: number;
}) => {
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const {
    cards,
    isUploading,
    isSubmitPending,
    isSubmitting,
    isAnalyzingOcr,
    isPollingTimedOut,
    canSubmit,
    form,
    uploadFiles,
    deleteFile,
    submitProof,
    resetPollingTimer,
  } = usePaymentProofOcrForm({
    accessToken,
    orderPaymentId,
    onSuccess,
    telemetryContext,
  });

  const manualOverrides = form.watch('manual_overrides');
  const remainingFileSlots = getRemainingOcrFileSlots(cards.length);
  const canOpenDropzone = remainingFileSlots > 0 && !isSubmitting;
  const isOcrUploadLoading = isOcrUploadLoadingState({
    isAnalyzingOcr,
    isPollingTimedOut,
    isUploading,
  });
  const visibleCards = getVisibleOcrCards(cards, isOcrUploadLoading);
  const { handleDropzoneReady } = useOcrDropzoneOpenRequest({
    canOpenDropzone,
    openUploadRequestKey,
    seedLastRequestKey: 0,
  });

  const handleFilesSelected = useCallback(
    (selected: File[]) => {
      onUploadStarted?.();
      uploadFiles(selected);
    },
    [onUploadStarted, uploadFiles]
  );

  return (
    <FormProvider {...form}>
      <OcrUploadLoadingModal isOpen={isOcrUploadLoading} />

      <Stack data-testid="payment-instruction-proof-panel" spacing={3} w="100%">
        {isPollingTimedOut && (
          <Flex
            align="center"
            bg="#FFF9E1"
            borderRadius="4px"
            gap={3}
            px={4}
            py={2}
          >
            <Text color="#131415" flex={1} fontSize="12px">
              Bukti transfer masih dianalisa, coba lagi nanti.
            </Text>
            <Button
              color="blue"
              onClick={resetPollingTimer}
              size="sm"
              variant="link"
            >
              Refresh
            </Button>
          </Flex>
        )}

        <OcrStagedCards
          bankFieldLabel="Bank Pengirim"
          cards={visibleCards}
          deletingId={deletingId}
          layout="row"
          manualOverrides={manualOverrides}
          w="100%"
          onDelete={(id) => {
            setDeletingId(id);
            deleteFile(id).finally(() => setDeletingId(null));
          }}
        />

        <OcrStagedDropzone
          isSubmitting={isSubmitting}
          maxW={INSTRUCTION_DROPZONE_MAX_W}
          remainingFileSlots={remainingFileSlots}
          onFilesSelected={handleFilesSelected}
          onReady={handleDropzoneReady}
        />

        <Flex justify="center" pt={1} w="100%">
          <SharedButton
            bg="#0941A8"
            borderRadius="8px"
            color="white"
            fontSize="14px"
            fontWeight={500}
            h="48px"
            isDisabled={!canSubmit || isSubmitting}
            isLoading={isSubmitPending}
            minW="240px"
            variant="solid"
            width="240px"
            _hover={{ bg: '#0941A8' }}
            onClick={submitProof}
          >
            <span>Kirim</span>
          </SharedButton>
        </Flex>
      </Stack>
    </FormProvider>
  );
};

const PaymentInstructionManualForm = ({
  accessToken,
  onSuccess,
  orderPaymentId,
  telemetryContext,
}: Omit<
  PaymentInstructionProofPanelProps,
  'mode' | 'openUploadRequestKey'
>) => {
  const {
    form,
    isSubmitting,
    handleSubmit,
    handleRemoveFile,
    files,
    hasNewFiles,
  } = usePaymentProofForm({
    accessToken,
    paymentId: orderPaymentId,
    skipSuccessToast: true,
    telemetryContext,
    onSuccess,
  });

  const bankAccountLength = form.watch('bank_account')?.length ?? 0;

  return (
    <FormProvider {...form}>
      <Stack
        as="form"
        bg="white"
        borderColor="#D9D9D9"
        borderRadius="8px"
        borderWidth="1px"
        data-testid="payment-instruction-proof-panel"
        onSubmit={form.handleSubmit(handleSubmit)}
        px={4}
        py={4}
        pb={6}
        spacing={4}
        w="100%"
      >
        <Flex
          align="center"
          bg="#FFF9E1"
          borderRadius="4px"
          gap={2}
          px={4}
          py={2}
        >
          <Icon as={InfoOutlined} boxSize="20px" color="#E28800" />
          <Text color="#131415" fontSize="12px" lineHeight="15px">
            Segera unggah bukti pembayaran agar PO diproses
          </Text>
        </Flex>

        <Flex direction={{ base: 'column', md: 'row' }} gap={6} w="100%">
          <Box flex="1">
            <Text
              color="#131415"
              fontSize="16px"
              fontWeight={600}
              lineHeight="19px"
              mb={3}
            >
              Nama Bank
            </Text>
            <Select
              {...form.register('bank_name')}
              borderColor="gray.33"
              borderRadius="8px"
              h="48px"
              placeholder="Masukkan nama bank"
            >
              {BANK_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Box>
          <Box flex="1">
            <TextField
              border="1px solid"
              borderColor="gray.33"
              errors={form.formState.errors}
              h="48px"
              label="Nama Pemilik Rekening"
              maxLength={100}
              name="bank_account"
              placeholder="Masukkan nama"
              register={form.register}
              labelProps={{
                color: '#131415',
                fontSize: '16px',
                fontWeight: 600,
                lineHeight: '19px',
                mb: '12px',
              }}
            />
            <Text color="#999999" fontSize="14px" fontWeight={500} mt={2}>
              {bankAccountLength}/100
            </Text>
          </Box>
        </Flex>

        {files.length > 0 && (
          <Stack spacing={2}>
            {files.map((file, index) => (
              <Flex
                key={`${file.name}-${file.size}`}
                align="center"
                gap={3}
                minW={0}
                w="100%"
              >
                <Text
                  color="#373F41"
                  flex="1"
                  fontSize="14px"
                  isTruncated
                  minW={0}
                >
                  {file.name}
                </Text>
                <Button
                  color="#8E8E93"
                  flexShrink={0}
                  fontSize="14px"
                  fontWeight={500}
                  h="32px"
                  minW="64px"
                  px={3}
                  size="sm"
                  variant="ghost"
                  whiteSpace="nowrap"
                  onClick={() => handleRemoveFile(index)}
                >
                  Hapus
                </Button>
              </Flex>
            ))}
          </Stack>
        )}

        {files.length < 5 && (
          <DropzoneOcr
            disabled={isSubmitting}
            maxFiles={Math.max(5 - files.length, 0)}
            maxSizeMb={5}
            maxW={INSTRUCTION_DROPZONE_MAX_W}
            onFilesSelected={(selected) => {
              const current = form.getValues('bukti_bayar') ?? [];
              form.setValue('bukti_bayar', [...current, ...selected], {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
          />
        )}

        <Flex justify="center" pt={1} w="100%">
          <SharedButton
            bg="#0941A8"
            borderRadius="8px"
            color="white"
            fontSize="14px"
            fontWeight={500}
            h="48px"
            isDisabled={!hasNewFiles || isSubmitting}
            isLoading={isSubmitting}
            minW="240px"
            type="submit"
            variant="solid"
            width="240px"
            _hover={{ bg: '#0941A8' }}
          >
            <span>Kirim</span>
          </SharedButton>
        </Flex>
      </Stack>
    </FormProvider>
  );
};
